import api from './api.service';

export interface SalesTeam {
  id: string;
  company_id: string;
  team_code: string;
  team_name: string;
  description?: string | null;
  manager_user_id?: string | null;
  region?: string | null;
  business_vertical?: string | null;
  is_active: boolean;
}

export interface SalesTeamMembership {
  id: string;
  sales_team_id: string;
  user_id: string;
  role_in_team: string;
  is_active: boolean;
  effective_from?: string | null;
  effective_to?: string | null;
}

export interface SalesTeamPipelineRow {
  team_id: string;
  team_name: string;
  open_opportunities: number;
  pipeline_value: number;
  open_quotations: number;
  quotation_value?: number;
  draft_sales_orders: number;
  booked_sales_orders?: number;
  booked_sales_order_value?: number;
  draft_boqs?: number;
  draft_tre_estimations?: number;
}

export interface SalesTeamCommandCenterExceptions {
  missing_team_count: number;
  missing_by_type?: Record<string, number>;
  overdue_follow_ups: number;
  stale_opportunities: number;
}

export interface SalesTeamCommandCenterWorkQueue {
  pending_approvals: number;
  open_tasks: number;
}

export interface SalesTeamCommandCenterData {
  teams: SalesTeamPipelineRow[];
  pipeline_by_stage?: SalesTeamPipelineRow[];
  exceptions: SalesTeamCommandCenterExceptions;
  work_queue: SalesTeamCommandCenterWorkQueue;
  generated_at?: string;
}

export interface SalesTeamFunnelRow {
  stage: string;
  cnt: number;
  value_sum: number;
}

export interface SalesTeamAnalyticsData {
  funnel: SalesTeamFunnelRow[];
  conversion_pct: number;
  team_comparison?: SalesTeamPipelineRow[];
  exceptions?: SalesTeamCommandCenterExceptions;
  work_queue?: SalesTeamCommandCenterWorkQueue;
}

export async function listAssignableSalesTeams(): Promise<SalesTeam[]> {
  const res = await api.get('/sales/teams/assignable');
  const body = (res as { data?: SalesTeam[] })?.data ?? res;
  return Array.isArray(body) ? body : (body as { data?: SalesTeam[] })?.data ?? [];
}

export async function listSalesTeams(onlyActive = true): Promise<SalesTeam[]> {
  const res = await api.get(`/sales/teams?only_active=${onlyActive}`);
  const body = (res as { data?: SalesTeam[] })?.data ?? res;
  return Array.isArray(body) ? body : (body as { data?: SalesTeam[] })?.data ?? [];
}

export async function createSalesTeam(payload: Partial<SalesTeam>): Promise<SalesTeam> {
  const res = await api.post('/sales/teams', payload);
  return ((res as { data?: SalesTeam })?.data ?? res) as SalesTeam;
}

export async function updateSalesTeam(id: string, payload: Partial<SalesTeam>): Promise<SalesTeam> {
  const res = await api.put(`/sales/teams/${id}`, payload);
  return ((res as { data?: SalesTeam })?.data ?? res) as SalesTeam;
}

export async function listTeamMemberships(teamId: string): Promise<SalesTeamMembership[]> {
  const res = await api.get(`/sales/teams/${teamId}/memberships`);
  const body = (res as { data?: SalesTeamMembership[] })?.data ?? res;
  return Array.isArray(body) ? body : (body as { data?: SalesTeamMembership[] })?.data ?? [];
}

export async function addTeamMembership(
  teamId: string,
  payload: { user_id: string; role_in_team?: string }
): Promise<SalesTeamMembership> {
  const res = await api.post(`/sales/teams/${teamId}/memberships`, payload);
  return ((res as { data?: SalesTeamMembership })?.data ?? res) as SalesTeamMembership;
}

export async function removeTeamMembership(membershipId: string): Promise<void> {
  await api.delete(`/sales/teams/memberships/${membershipId}`);
}

export interface MissingTeamRecord {
  id: string;
  entity_type?: string;
  document_number?: string;
  customer_label?: string;
  owner_user_id?: string;
  created_at?: string;
  suggested_team_id?: string | null;
  suggested_team_name?: string | null;
  sales_team_id: string | null;
}

export async function listMissingTeamRecords(
  entityType: string,
  limit = 50
): Promise<MissingTeamRecord[]> {
  const res = await api.get('/sales/teams/admin/missing-team-records', {
    params: { entity_type: entityType, limit },
  });
  const body = (res as { data?: MissingTeamRecord[] })?.data ?? res;
  const rows = Array.isArray(body) ? body : (body as { data?: MissingTeamRecord[] })?.data;
  return rows ?? [];
}

export async function bulkAssignSalesTeams(payload: {
  items: Array<{ entity_type: string; entity_id: string; sales_team_id: string }>;
}): Promise<{ updated: number }> {
  const res = await api.post('/sales/teams/admin/bulk-assign', payload);
  const body = (res as { data?: { updated: number } })?.data ?? res;
  return (body as { updated: number }) ?? { updated: 0 };
}

export interface SalesTeamTransferRow {
  id: string;
  company_id: string;
  entity_type: string;
  entity_id: string;
  document_number?: string | null;
  from_team_id?: string | null;
  from_team_name?: string | null;
  to_team_id: string;
  to_team_name?: string | null;
  reason?: string | null;
  remarks?: string | null;
  transfer_downstream: boolean;
  transfer_date: string;
  created_at: string;
}

export async function transferSalesRecord(payload: {
  entity_type: string;
  entity_id: string;
  to_team_id: string;
  reason?: string;
  remarks?: string;
  transfer_downstream?: boolean;
}): Promise<unknown> {
  const res = await api.post('/sales/teams/transfer', payload);
  return (res as { data?: unknown })?.data ?? res;
}

export async function listSalesTeamTransferHistory(
  entityType: string,
  entityId: string
): Promise<SalesTeamTransferRow[]> {
  const res = await api.get('/sales/teams/transfers/history', {
    params: { entity_type: entityType, entity_id: entityId },
  });
  const body = (res as { data?: SalesTeamTransferRow[] })?.data ?? res;
  return Array.isArray(body) ? body : (body as { data?: SalesTeamTransferRow[] })?.data ?? [];
}

export async function getSalesTeamCommandCenter(params?: {
  team_id?: string;
  date_from?: string;
  date_to?: string;
}): Promise<SalesTeamCommandCenterData> {
  const res = await api.get<SalesTeamCommandCenterData>('/sales/teams/command-center', { params });
  return (res.data ?? res) as SalesTeamCommandCenterData;
}

export async function getSalesTeamAnalytics(params?: {
  team_id?: string;
}): Promise<SalesTeamAnalyticsData> {
  const res = await api.get<SalesTeamAnalyticsData>('/sales/teams/analytics', { params });
  return (res.data ?? res) as SalesTeamAnalyticsData;
}

export async function listSalesTeamTransferRegister(params: {
  entity_type?: string;
  team_id?: string;
  page?: number;
  page_size?: number;
}): Promise<{ data: SalesTeamTransferRow[]; total: number; page: number; pageSize: number }> {
  const res = await api.get('/sales/teams/transfers/register', { params });
  const body = (res as { data?: { data: SalesTeamTransferRow[]; total: number; page: number; pageSize: number } })
    ?.data ?? res;
  const payload = (body as { data?: SalesTeamTransferRow[]; total?: number; page?: number; pageSize?: number }) ?? {};
  return {
    data: payload.data ?? [],
    total: payload.total ?? 0,
    page: payload.page ?? 1,
    pageSize: payload.pageSize ?? 25,
  };
}
